import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query, where, limit } from 'firebase/firestore';
import { BrandGradient } from '../components/common/PFILogo';
import { 
  BellIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  BuildingStorefrontIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  EyeIcon,
  TrashIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const notificationTypes = {
    'quote-received': {
      icon: EnvelopeIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    'quote-accepted': {
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    'quote-declined': {
      icon: XMarkIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    'project-update': {
      icon: InformationCircleIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    'task-completed': {
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    'deadline-approaching': {
      icon: ExclamationTriangleIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    'payment-received': {
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    'system': {
      icon: InformationCircleIcon,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      // Load real notifications from Firebase
      const notificationsSnapshot = await getDocs(collection(db, 'notifications'));
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      
      // Sort by date, newest first
      notificationsData.sort((a, b) => b.createdAt - a.createdAt);
      
      setNotifications(notificationsData);

    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = async () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const deleteNotification = async (notificationId) => {
    setNotifications(prev => 
      prev.filter(notif => notif.id !== notificationId)
    );
  };

  const deleteSelected = async () => {
    if (selectedNotifications.length === 0) return;
    
    setNotifications(prev => 
      prev.filter(notif => !selectedNotifications.includes(notif.id))
    );
    setSelectedNotifications([]);
  };

  const toggleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const selectAll = () => {
    const visibleIds = filteredNotifications.map(n => n.id);
    setSelectedNotifications(visibleIds);
  };

  const deselectAll = () => {
    setSelectedNotifications([]);
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    switch (filter) {
      case 'unread':
        return !notif.read;
      case 'read':
        return notif.read;
      case 'quotes':
        return ['quote-received', 'quote-accepted', 'quote-declined'].includes(notif.type);
      case 'projects':
        return ['project-update', 'task-completed', 'deadline-approaching'].includes(notif.type);
      default:
        return true;
    }
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="w-5 h-5 text-pink-200" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 bg-white bg-opacity-20 text-gray-900 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent"
                >
                  <option value="all" className="text-gray-900">All Notifications</option>
                  <option value="unread" className="text-gray-900">Unread</option>
                  <option value="read" className="text-gray-900">Read</option>
                  <option value="quotes" className="text-gray-900">Quotes</option>
                  <option value="projects" className="text-gray-900">Projects</option>
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {selectedNotifications.length > 0 && (
                <button
                  onClick={deleteSelected}
                  className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Selected ({selectedNotifications.length})
                </button>
              )}
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors"
              >
                Mark All Read
              </button>
            </div>
          </div>
          
          <div className="flex items-start">
            <BellIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Notifications</h1>
              <p className="text-pink-100 text-xl">
                Stay updated on project progress and system events
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <BellIcon className="w-5 h-5 mr-2" />
                  {notifications.length} Total Notifications
                </div>
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                  {unreadCount} Unread
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Bulk Actions */}
        {notifications.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
                    onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
                    className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <label className="text-sm text-gray-700">Select All</label>
                </div>
                <span className="text-sm text-gray-500">
                  {selectedNotifications.length} of {filteredNotifications.length} selected
                </span>
              </div>
              
              {selectedNotifications.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      selectedNotifications.forEach(id => markAsRead(id));
                      setSelectedNotifications([]);
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Mark Read
                  </button>
                  <button
                    onClick={deleteSelected}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.map((notification) => {
            const typeConfig = notificationTypes[notification.type] || notificationTypes.system;
            const IconComponent = typeConfig.icon;
            
            return (
              <div
                key={notification.id}
                className={`bg-white rounded-2xl shadow-xl border p-6 transition-all duration-200 ${
                  notification.read ? 'opacity-75' : typeConfig.borderColor
                } ${selectedNotifications.includes(notification.id) ? 'ring-2 ring-pink-500' : ''}`}
              >
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(notification.id)}
                    onChange={() => toggleSelectNotification(notification.id)}
                    className="mt-1 w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  
                  <div className={`p-3 rounded-full ${typeConfig.bgColor} ${typeConfig.color}`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className={`text-lg font-semibold ${
                        notification.read ? 'text-gray-600' : 'text-gray-900'
                      }`}>
                        {notification.title}
                        {!notification.read && (
                          <span className="inline-block w-2 h-2 bg-blue-500 rounded-full ml-2"></span>
                        )}
                      </h3>
                      <div className="flex items-center space-x-2 ml-4">
                        <span className="text-sm text-gray-500 whitespace-nowrap">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Mark as read"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete notification"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <p className={`mb-3 ${
                      notification.read ? 'text-gray-500' : 'text-gray-700'
                    }`}>
                      {notification.message}
                    </p>
                    
                    {/* Project/Supplier Info */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      {notification.projectName && (
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          <span>{notification.projectName}</span>
                        </div>
                      )}
                      {notification.supplierName && (
                        <div className="flex items-center">
                          <BuildingStorefrontIcon className="w-4 h-4 mr-1" />
                          <span>{notification.supplierName}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Button */}
                    {notification.actionUrl && (
                      <Link
                        to={notification.actionUrl}
                        onClick={() => markAsRead(notification.id)}
                        className="inline-flex items-center px-4 py-2 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                      >
                        <EyeIcon className="w-4 h-4 mr-2" />
                        View Details
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {filteredNotifications.length === 0 && (
            <div className="text-center py-12">
              <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'unread' 
                  ? 'No unread notifications' 
                  : filter === 'read' 
                  ? 'No read notifications'
                  : 'No notifications'}
              </h3>
              <p className="text-gray-500">
                {filter === 'all' 
                  ? 'You\'re all caught up! New notifications will appear here.' 
                  : `No ${filter} notifications at this time.`}
              </p>
            </div>
          )}
        </div>

        {/* Notification Stats */}
        {notifications.length > 0 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <div className="text-2xl font-bold text-blue-600">
                {notifications.filter(n => n.type.includes('quote')).length}
              </div>
              <div className="text-sm text-gray-600">Quote Notifications</div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <div className="text-2xl font-bold text-green-600">
                {notifications.filter(n => ['project-update', 'task-completed'].includes(n.type)).length}
              </div>
              <div className="text-sm text-gray-600">Project Updates</div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6">
              <div className="text-2xl font-bold text-orange-600">
                {notifications.filter(n => n.type === 'deadline-approaching').length}
              </div>
              <div className="text-sm text-gray-600">Deadline Alerts</div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
              <div className="text-2xl font-bold text-purple-600">
                {Math.round((notifications.filter(n => n.read).length / notifications.length) * 100) || 0}%
              </div>
              <div className="text-sm text-gray-600">Read Rate</div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
